// src/pages/Publicacoes.tsx
import React, { useState } from 'react';
import { Calendar, User, ArrowRight, FileText, Award, Users } from 'lucide-react';
import Button from '../components/Common/Button';

interface Publicacao {
  id: string;
  tipo: 'noticia' | 'artigo' | 'evento' | 'homologacao';
  titulo: string;
  descricao: string;
  conteudo?: string;
  autor: string;
  data: string;
  imagem?: string;
  link?: string;
}

const Publicacoes: React.FC = () => {
  const [tipoSelecionado, setTipoSelecionado] = useState('todos');

  const publicacoes: Publicacao[] = [
    {
      id: '1',
      tipo: 'noticia',
      titulo: 'Federação Expande Presença em Novo Estado',
      descricao: 'Abertura de nova filial no estado de Goiás marca expansão regional',
      conteudo: 'Com o objetivo de ampliar o acesso aos serviços da Federação...',
      autor: 'Redação Federação',
      data: '2025-12-20',
    },
    {
      id: '2',
      tipo: 'artigo',
      titulo: 'Tendências de Beleza para 2025',
      descricao: 'Análise das principais tendências que dominarão o mercado de beleza',
      conteudo: 'Conheça as tendências que irão marcar presença em 2025...',
      autor: 'Dra. Paula Ribeiro',
      data: '2025-12-18',
    },
    {
      id: '3',
      tipo: 'evento',
      titulo: 'Congresso Nacional de Profissionais de Beleza 2025',
      descricao: 'Maior encontro anual da comunidade de profissionais de beleza',
      conteudo: 'Palestras, workshops e networking com profissionais...',
      autor: 'Federação',
      data: '2025-12-25',
    },
    {
      id: '4',
      tipo: 'homologacao',
      titulo: 'Novo Curso Homologado: Harmonização Facial',
      descricao: 'Curso de harmonização facial da Escola XYZ foi aprovado',
      conteudo: 'O curso atende a todos os critérios de qualidade...',
      autor: 'Comissão de Avaliação',
      data: '2025-12-15',
    },
    {
      id: '5',
      tipo: 'noticia',
      titulo: 'Regulamentação Atualizada para Profissionais',
      descricao: 'Novas normas entram em vigor a partir de janeiro',
      conteudo: 'A Federação comunicou as atualizações...',
      autor: 'Redação Federação',
      data: '2025-12-12',
    },
    {
      id: '6',
      tipo: 'artigo',
      titulo: 'Como Iniciar Sua Carreira como Freelancer',
      descricao: 'Guia completo para profissionais que desejam trabalhar por conta própria',
      conteudo: 'Trabalhar como freelancer oferece flexibilidade...',
      autor: 'Carlos Mendes',
      data: '2025-12-10',
    },
  ];

  const publicacoesFiltradas = tipoSelecionado === 'todos' 
    ? publicacoes 
    : publicacoes.filter((p) => p.tipo === tipoSelecionado);

  const tipoConfig = {
    noticia: { icon: FileText, cor: 'text-color-primary', bg: 'bg-color-primary/10' },
    artigo: { icon: Users, cor: 'text-color-secondary', bg: 'bg-color-secondary/10' },
    evento: { icon: Calendar, cor: 'text-color-success', bg: 'bg-color-success/10' },
    homologacao: { icon: Award, cor: 'text-color-warning', bg: 'bg-color-warning/10' },
  };

  return (
    <div className="min-h-screen bg-color-background">
      {/* HERO */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-color-primary to-color-secondary text-white">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="text-4xl md:text-5xl font-bold mb-4">Publicações</h1>
          <p className="text-lg text-white/90 max-w-2xl mx-auto">
            Notícias, artigos e informações importantes para profissionais de beleza.
          </p>
        </div>
      </section>

      {/* FILTROS */}
      <section className="py-8 bg-color-surface border-b border-color-border">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex flex-wrap gap-3 justify-center">
            {[
              { value: 'todos', label: 'Tudo' },
              { value: 'noticia', label: 'Notícias' },
              { value: 'artigo', label: 'Artigos' },
              { value: 'evento', label: 'Eventos' },
              { value: 'homologacao', label: 'Homologações' },
            ].map((filtro) => (
              <button
                key={filtro.value}
                onClick={() => setTipoSelecionado(filtro.value)}
                className={`px-6 py-2 rounded-full font-semibold transition-all ${
                  tipoSelecionado === filtro.value
                    ? 'bg-color-primary text-white'
                    : 'bg-color-background border border-color-border text-color-text hover:bg-color-border'
                }`}
              >
                {filtro.label}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* PUBLICAÇÕES */}
      <section className="py-16 md:py-24 bg-color-surface">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8">
          <div className="space-y-6">
            {publicacoesFiltradas.map((pub) => {
              const config = tipoConfig[pub.tipo];
              const Icon = config.icon;

              return (
                <div
                  key={pub.id}
                  className="bg-color-background border border-color-border rounded-lg overflow-hidden hover:shadow-lg transition-all flex flex-col md:flex-row"
                >
                  {/* Imagem/Icone */}
                  <div className={`w-full md:w-40 h-40 ${config.bg} flex items-center justify-center`}>
                    <Icon className={config.cor} size={48} />
                  </div>

                  {/* Conteúdo */}
                  <div className="flex-1 p-6 flex flex-col">
                    <div className="flex items-center gap-4 mb-3">
                      <span className={`text-xs px-3 py-1 rounded-full font-semibold ${config.bg} ${config.cor} capitalize`}>
                        {pub.tipo}
                      </span>
                      <span className="text-sm text-color-text-secondary flex items-center gap-2">
                        <Calendar size={14} />
                        {new Date(pub.data).toLocaleDateString('pt-BR')}
                      </span>
                    </div>

                    <h3 className="text-xl font-bold text-color-text mb-2">{pub.titulo}</h3>
                    <p className="text-color-text-secondary mb-4">{pub.descricao}</p>

                    <div className="flex items-center justify-between mt-auto">
                      <span className="text-sm text-color-text-secondary flex items-center gap-2">
                        <User size={14} />
                        {pub.autor}
                      </span>
                      <Button variant="outline" size="sm">
                        Ler Mais
                        <ArrowRight size={14} />
                      </Button>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>

          {publicacoesFiltradas.length === 0 && (
            <div className="text-center py-12">
              <FileText size={48} className="mx-auto mb-4 text-color-text-secondary opacity-50" />
              <p className="text-color-text-secondary">Nenhuma publicação encontrada</p>
            </div>
          )}
        </div>
      </section>

      {/* NEWSLETTER */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-color-primary/10 to-color-secondary/10">
        <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-2xl text-center">
          <h2 className="text-3xl font-bold text-color-text mb-4">
            Receba Atualizações
          </h2>
          <p className="text-color-text-secondary mb-8">
            Inscreva-se na nossa newsletter para receber novidades e publicações direto no seu email.
          </p>

          <div className="flex flex-col sm:flex-row gap-3">
            <input
              type="email"
              placeholder="seu@email.com"
              className="flex-1 px-4 py-3 border border-color-border rounded-lg bg-white text-color-text focus:outline-none focus:ring-2 focus:ring-color-primary"
            />
            <Button variant="primary" size="md">
              Inscrever-se
            </Button>
          </div>
        </div>
      </section>
    </div>
  );
};

export default Publicacoes;
